import { inject, observer } from "mobx-react";
import { ArrowRight, RefreshCw, Search } from "lucide-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import {
  useGetV4PartnerByApplicationIdAccountManufacturerQuery,
  type SalesData as ApiSalesData,
} from "randmar-api-client";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Progress } from "randmarcomps";
import { Link } from "react-router-dom";
import OpenOrdersGrid from "../components/grids/OpenOrdersGrid";
import CurrencyUtils from "../components/util/CurrencyUtils";
import withManufacturer from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { OrderDetail } from "../stores/document/Document";
import { RootStore } from "../stores/rootStore";
import withRoot from "../withRoot";

interface IProps {
  store?: RootStore;
  applicationId: string;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

type SalesDataField = Extract<keyof ApiSalesData, "SalesLast30Days" | "SalesLast3Months" | "SalesYearToDay">;

interface TimeframeConfig {
  id: string;
  label: string;
  days: number;
  salesField?: SalesDataField;
}

const TIMEFRAME_CONFIGS: TimeframeConfig[] = [
  { id: "30", label: "Last 30 days", days: 30, salesField: "SalesLast30Days" },
  { id: "90", label: "Last 3 months", days: 90, salesField: "SalesLast3Months" },
  { id: "365", label: "Last 12 months", days: 365, salesField: "SalesYearToDay" },
];

const parseDateInt = (value?: number) => {
  if (!value) return undefined;
  const stringValue = value.toString();
  if (stringValue.length !== 8) return undefined;

  const year = Number(stringValue.slice(0, 4));
  const month = Number(stringValue.slice(4, 6)) - 1;
  const day = Number(stringValue.slice(6, 8));

  if ([year, month, day].some(part => Number.isNaN(part))) {
    return undefined;
  }

  return new Date(Date.UTC(year, month, day));
};

const getLineAmount = (order: OrderDetail) => {
  const qty = order.quantity ?? 0;
  const unitPrice = order.unitPrice ?? 0;
  const extended = order.extendedPrice ?? qty * unitPrice;
  return Number.isFinite(extended) ? extended : 0;
};

const CompletedOrderDetails: React.FC<IProps> = ({ store, applicationId }) => {
  const [loading, setLoading] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null);
  const {
    data: manufacturer,
    isLoading: isManufacturerLoading,
    isFetching: isManufacturerFetching,
  } = useGetV4PartnerByApplicationIdAccountManufacturerQuery(
    { applicationId, withSpecification: true },
    { skip: !applicationId }
  );
  const salesData = manufacturer?.SalesData;

  const fetchData = React.useCallback(async () => {
    if (!store) return;
    setLoading(true);
    await store.documentsStore.getCompletedOrders(applicationId);
    setLastUpdated(new Date());
    setLoading(false);
  }, [applicationId, store]);

  React.useEffect(() => {
    void fetchData();
  }, [fetchData]);

  if (!store) {
    return <Progress />;
  }

  const { documentsStore, warehousesStore } = store;
  const completedOrders = documentsStore.completedOrders ?? [];
  const isSalesDataLoading = Boolean(applicationId) && (isManufacturerLoading || isManufacturerFetching);
  const isPageLoading = loading || isSalesDataLoading;

  const sortedCompletedOrders = React.useMemo(() =>
    completedOrders
      .slice()
      .sort(
        (a, b) =>
          (b.orderNumber || "").localeCompare(a.orderNumber || "") ||
          (a.mpn || "").localeCompare(b.mpn || "")
      ),
    [completedOrders]
  );

  const filteredOrders = React.useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return sortedCompletedOrders;

    return sortedCompletedOrders.filter(order => {
      const locationName = warehousesStore?.getWarehouseNameFromCode(order.warehouseCode) || "";
      const haystack = [
        order.resellerName,
        order.orderNumber,
        order.poNumber,
        order.mpn,
        order.randmarSKU,
        order.warehouseCode,
        locationName,
      ]
        .filter(Boolean)
        .map(value => value!.toString().toLowerCase());

      return haystack.some(value => value.includes(normalized));
    });
  }, [searchTerm, sortedCompletedOrders, warehousesStore]);

  const timeframeOrderStats = React.useMemo(() => {
    const now = Date.now();
    return TIMEFRAME_CONFIGS.reduce<Record<string, number>>((acc, { id, days }) => {
      const cutoff = now - days * MS_PER_DAY;
      let amount = 0;

      sortedCompletedOrders.forEach(order => {
        const orderDate = parseDateInt(order.orderDate ?? order.documentDate);
        if (!orderDate) return;
        if (orderDate.getTime() >= cutoff) {
          amount += getLineAmount(order);
        }
      });

      acc[id] = amount;
      return acc;
    }, {});
  }, [sortedCompletedOrders]);

  const timeframeStats = React.useMemo(() => {
    return TIMEFRAME_CONFIGS.map(({ id, label, salesField }) => {
      const fallbackAmount = timeframeOrderStats[id] ?? 0;
      let apiAmount: number | undefined;
      if (salesField && salesData && typeof salesData[salesField] === "number") {
        apiAmount = salesData[salesField] ?? undefined;
      }

      return {
        id,
        label,
        amount: apiAmount ?? fallbackAmount,
      };
    });
  }, [salesData, timeframeOrderStats]);

  const summaryTotals = React.useMemo(() => {
    return filteredOrders.reduce(
      (acc, order) => {
        acc.amount += getLineAmount(order);
        acc.units += order.quantity ?? 0;
        return acc;
      },
      { amount: 0, units: 0 }
    );
  }, [filteredOrders]);

  const lastHundredStats = React.useMemo(() => {
    const ordersByRecency = completedOrders
      .slice()
      .sort((a, b) => {
        const aDate = parseDateInt(a.orderDate ?? a.documentDate);
        const bDate = parseDateInt(b.orderDate ?? b.documentDate);
        if (aDate && bDate) {
          return bDate.getTime() - aDate.getTime();
        }
        if (aDate) return -1;
        if (bDate) return 1;
        return 0;
      })
      .slice(0, 100);

    return ordersByRecency.reduce((total, order) => {
      total += getLineAmount(order);
      return total;
    }, 0);
  }, [completedOrders]);

  const salesStatCards = React.useMemo(
    () => [
      {
        id: "last-100",
        label: "Last 100 orders",
        amount: lastHundredStats,
      },
      ...timeframeStats,
    ],
    [lastHundredStats, timeframeStats]
  );

  const lastUpdatedLabel = lastUpdated
    ? lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <main className="space-y-6">
      {isPageLoading ? <Progress /> : <Progress value={100} />}

      <Card className="border-0 bg-gradient-to-r from-[#f6eeff] via-[#f2ecff] to-[#fdf8ff] shadow-[0_20px_60px_rgba(77,22,122,0.15)]">
        <CardContent className="space-y-8 p-6 sm:p-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-3">
              <span className="inline-flex items-center rounded-full bg-white/70 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-[#5c2f91]">
                Manufacturer Portal
              </span>
              <CardTitle className="text-3xl font-extrabold text-[#2c1848]">Completed Orders</CardTitle>
              <CardDescription className="text-base text-[#5b5170]">
                See trends and order details from past orders.
              </CardDescription>
            </div>

            <div className="flex w-full flex-col gap-3 text-sm text-[#4a3b65] lg:w-auto lg:items-end lg:text-right">
              <div className="inline-flex items-center gap-3 rounded-full border border-white/80 bg-white px-4 py-2 font-semibold text-[#5e2e9e] shadow-[0_12px_30px_rgba(94,46,158,0.15)] self-start lg:self-end">
                <span
                  aria-hidden
                  className={`h-2.5 w-2.5 rounded-full border border-white shadow-[0_0_0_4px_rgba(94,57,163,0.12)] ${
                    loading ? "bg-[var(--color-randmar-yellow,#fbbc05)]" : "bg-[var(--color-randmar-green,#1bb55c)]"
                  }`}
                />
                <span>{lastUpdatedLabel ? `Last updated at ${lastUpdatedLabel}` : "Syncing latest data"}</span>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row lg:self-end">
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full gap-2 rounded-full border border-[#c7b3ef] bg-white/80 text-[#5e2e9e] shadow-sm transition hover:-translate-y-0.5 hover:border-[#a37adb] hover:bg-white"
                  onClick={() => void fetchData()}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                  Refresh data
                </Button>
                <Button
                  asChild
                  variant="purple"
                  className="w-full rounded-full px-6 font-semibold text-white shadow-md transition hover:-translate-y-0.5"
                >
                  <Link
                    to={Path.orderDetails(applicationId)}
                    aria-label="View open orders"
                    className="flex items-center justify-center gap-2 text-white"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/25">
                      <ArrowRight className="h-3 w-3 text-white" aria-hidden />
                    </span>
                    View Open Orders
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {salesStatCards.map(stat => (
              <Card
                key={stat.id}
                className="rounded-2xl border border-white/60 bg-white/90 p-5 shadow-[0_18px_45px_rgba(105,50,198,0.08)]"
              >
                <CardHeader className="space-y-1 p-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8a76b4]">{stat.label}</p>
                  <CardTitle className="text-3xl font-bold text-[#2f1f4c]">
                    <FormattedNumber value={stat.amount} {...CurrencyUtils.format} />
                  </CardTitle>
                </CardHeader>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <section className="space-y-4 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              placeholder="Search by reseller name, order number, reseller PO, MPN, or location"
              aria-label="Search completed orders"
              className="w-full rounded-2xl border-slate-200 bg-slate-50 pl-10 text-sm"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-[var(--ops-accent,#6f3dd1)]"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <p className="text-sm text-slate-500">
          Showing{" "}
          <span className="font-semibold text-slate-900">
            {filteredOrders.length}
          </span>{" "}
          of {sortedCompletedOrders.length} completed orders totaling{" "}
          <span className="font-semibold text-slate-900">
            {CurrencyUtils.formatNumber(summaryTotals.amount)}
          </span>{" "}
          and{" "}
          <span className="font-semibold text-slate-900">
            <FormattedNumber value={summaryTotals.units} />
          </span>{" "}
          units.
        </p>
      </section>

      <section className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-[#2f1f4c]">Detailed timeline</h2>
            <p className="text-sm text-slate-500">Sortable grid of every completed item grouped by order number.</p>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#f4edff] px-4 py-1 text-xs font-semibold uppercase tracking-wide text-[#5e2e9e]">
            ● {lastUpdatedLabel ? `Updated at ${lastUpdatedLabel}` : "Updated moments ago"}
          </span>
        </div>
        <OpenOrdersGrid productOrders={filteredOrders} withHeader variant="timeline" />
      </section>
    </main>
  );
};

export default withRoot(withManufacturer(inject("store")(observer(CompletedOrderDetails))));
